/**
 * The arithmetic under the console's charts — pure, so it can be tested.
 *
 * Money arrives as decimal STRINGS and stays that way everywhere it is shown
 * or sent; these helpers turn it into a number ONLY to size a bar or a meter,
 * which is drawing, not data.
 */

export function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  return Number(trimmed);
}

/** Spend over budget, or null when there is no budget to measure against. */
export function budgetRatio(spend: unknown, budget: unknown): number | null {
  const spent = toNumber(spend);
  const limit = toNumber(budget);
  if (spent === null || limit === null || limit <= 0) return null;
  return spent / limit;
}

export type MeterTone = 'ok' | 'warn' | 'bad';

/** From this share of the budget the meter turns amber; at the budget, red. */
const WARN_FROM = 0.8;

export function meterTone(ratio: number): MeterTone {
  if (ratio >= 1) return 'bad';
  if (ratio >= WARN_FROM) return 'warn';
  return 'ok';
}

function round(value: number): number {
  return Number(value.toFixed(10));
}

/**
 * Round-number axis ticks that span [min, max]. Always at least two, at most
 * a handful: the step is the smallest of 1·2·2.5·5·10 × 10ⁿ that keeps the
 * count near `count`.
 */
export function niceTicks(min: number, max: number, count: number): number[] {
  if (!(max > min)) return [round(min), round(min + 1)];
  const rough = (max - min) / Math.max(1, count - 1);
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * magnitude);
  const step = candidates.find((candidate) => candidate >= rough) ?? candidates[candidates.length - 1] ?? rough;
  const start = Math.floor(min / step) * step;
  const end = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let value = start; value <= end + step / 2; value += step) ticks.push(round(value));
  return ticks;
}

export interface Point {
  x: number;
  y: number;
}

export interface PlotBox {
  width: number;
  height: number;
  yMin: number;
  yMax: number;
}

function fmt(value: number): string {
  return String(Number(value.toFixed(2)));
}

/** Horizontal positions of the points across the box, oldest left. One point sits mid-box. */
export function xPositions(points: Point[], width: number): number[] {
  if (points.length === 0) return [];
  const xs = points.map((point) => point.x);
  const min = Math.min(...xs);
  const max = Math.max(...xs);
  if (max === min) return points.map(() => width / 2);
  return xs.map((x) => ((x - min) / (max - min)) * width);
}

export function yPosition(y: number, box: PlotBox): number {
  const span = box.yMax - box.yMin || 1;
  return box.height - ((y - box.yMin) / span) * box.height;
}

/** The SVG path for the series. A single point draws flat across the box so one entry still reads. */
export function linePath(points: Point[], box: PlotBox): string {
  if (points.length === 0) return '';
  const first = points[0];
  if (first === undefined) return '';
  const xs = xPositions(points, box.width);
  if (points.length === 1 || xs.every((x) => x === xs[0])) {
    const y = fmt(yPosition(first.y, box));
    return `M0,${y} L${fmt(box.width)},${y}`;
  }
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${fmt(xs[index] ?? 0)},${fmt(yPosition(point.y, box))}`)
    .join(' ');
}

/** Which point a pointer x is closest to; -1 for none. */
export function nearestIndex(xs: number[], x: number): number {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  xs.forEach((candidate, index) => {
    const distance = Math.abs(candidate - x);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
}
